import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Trash2, Pencil, TriangleAlert, Users2, ChevronDown, X } from "lucide-react";

export default function Memurlar() {
  const { user } = useAuth();
  const isSuperAdmin = user.role === "founder" || user.role === "admin";

  const [me, setMe] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [units, setUnits] = useState([]);
  const [oom, setOom] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [meRes, officersRes, ranksRes, unitsRes, oomRes] = await Promise.all([
      api.get("/memur/me"),
      api.get("/memur/officers"),
      api.get("/memur/ranks"),
      api.get("/memur/units"),
      api.get("/memur/officer-of-month"),
    ]);
    setMe(meRes.data);
    setOfficers(officersRes.data);
    setRanks(ranksRes.data);
    setUnits(unitsRes.data);
    setOom(oomRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- İzin yardımcıları (istemci tarafı — sunucu zaten gerçek denetimi yapıyor, bu sadece UI gösterimi) ---
  const myOfficer = me?.officer || null;
  const isOfficerManager = isSuperAdmin || me?.is_officer_manager;
  const isPs = isSuperAdmin || me?.is_ps;

  const canManageFull = (target) => {
    if (isSuperAdmin) return true;
    if (!myOfficer) return false;
    if (target.is_ps) return false;
    if (myOfficer.is_ps) return true;
    if (myOfficer.is_yps) return !target.is_yps;
    return false;
  };

  const canDeleteWarning = () => {
    if (isSuperAdmin) return true;
    if (!myOfficer) return false;
    if (myOfficer.is_ps || myOfficer.is_yps) return true;
    return myOfficer.units?.some((u) => u.position === "chief");
  };

  const canManageUnit = (unitId, targetPosition = "member") => {
    if (isSuperAdmin) return true;
    if (!myOfficer) return false;
    if (myOfficer.is_ps || myOfficer.is_yps || myOfficer.is_genel_birim_sefi) return true;
    const mine = myOfficer.units?.find((u) => u.unit_id === unitId);
    if (mine?.position === "chief") return true;
    if (mine?.position === "deputy_chief") return targetPosition === "member";
    return false;
  };

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return officers.filter((o) =>
      !s ||
      o.ic_isim?.toLowerCase().includes(s) ||
      o.discord_username?.toLowerCase().includes(s) ||
      o.hex_id?.toLowerCase().includes(s)
    );
  }, [officers, q]);

  if (loading) {
    return <div className="p-10 text-zinc-500 font-mono text-sm">YÜKLENİYOR...</div>;
  }

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-7xl">
      <header>
        <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// POLİS DEPARTMANI</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Memurlar</h1>
        <p className="text-zinc-500 mt-2">Rütbeler, birimler ve memur kadrosu yönetimi.</p>
      </header>

      <OfficerOfMonthBanner oom={oom} officers={officers} isPs={isPs} onChanged={load} />

      <Tabs defaultValue="memurlar">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="memurlar">Memurlar</TabsTrigger>
          {isOfficerManager && <TabsTrigger value="rutbeler">Rütbeler</TabsTrigger>}
          {isOfficerManager && <TabsTrigger value="birimler">Birimler</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="ayarlar">Ayarlar</TabsTrigger>}
        </TabsList>

        <TabsContent value="memurlar" className="mt-4 space-y-4">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: IC isim, discord, hex..."
            data-testid="memurlar-search-input" className="bg-zinc-950 border-zinc-800 max-w-md focus-visible:ring-rose-500" />

          <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/50">
                  <tr className="text-left text-[10px] font-mono text-zinc-500 tracking-widest">
                    <th className="px-4 py-3">IC İSİM</th>
                    <th className="px-4 py-3">DISCORD</th>
                    <th className="px-4 py-3">HEX ID</th>
                    <th className="px-4 py-3">RÜTBE</th>
                    <th className="px-4 py-3">BİRİM</th>
                    <th className="px-4 py-3">MAZERET</th>
                    <th className="px-4 py-3">RAPOR</th>
                    <th className="px-4 py-3">MUGSHOT</th>
                    <th className="px-4 py-3">COIN</th>
                    <th className="px-4 py-3">UYARI</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => (
                    <OfficerRow key={o.id} officer={o} ranks={ranks} units={units}
                      canFull={canManageFull(o)} canManageUnit={canManageUnit} canDeleteWarning={canDeleteWarning()}
                      isSuperAdmin={isSuperAdmin} onChanged={load} />
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={11} className="px-4 py-10 text-center text-zinc-500">Sonuç yok.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {isOfficerManager && (
          <TabsContent value="rutbeler" className="mt-4">
            <RanksPanel ranks={ranks} onChanged={load} />
          </TabsContent>
        )}

        {isOfficerManager && (
          <TabsContent value="birimler" className="mt-4">
            <UnitsPanel units={units} onChanged={load} />
          </TabsContent>
        )}

        {isSuperAdmin && (
          <TabsContent value="ayarlar" className="mt-4">
            <SettingsPanel ranks={ranks} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ============ AYIN MEMURU BANNER ============
function OfficerOfMonthBanner({ oom, officers, isPs, onChanged }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");

  const save = async () => {
    if (!selected) return;
    try {
      await api.put("/memur/officer-of-month", { officer_id: selected });
      toast.success("Ayın memuru güncellendi");
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <Card className="bg-gradient-to-r from-amber-950/40 via-zinc-950 to-zinc-950 border-amber-900/40 p-6 flex items-center justify-between flex-wrap gap-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-600/40 flex items-center justify-center">
          <Star className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <div className="text-[10px] font-mono text-amber-500 tracking-widest">AYIN MEMURU</div>
          <div className="text-xl font-heading font-bold">{oom?.officer_name || "— Henüz seçilmedi —"}</div>
        </div>
      </div>
      {isPs && (
        <Dialog open={open} onOpenChange={setOpen}>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="oom-edit-button"
            className="border-amber-800 text-amber-400 hover:bg-amber-950/30">Ayın Memurunu Seç</Button>
          <DialogContent className="bg-zinc-950 border-zinc-800">
            <DialogHeader><DialogTitle>Ayın Memurunu Seç</DialogTitle></DialogHeader>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="oom-select" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Memur seçin" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {officers.map((o) => <SelectItem key={o.id} value={o.id}>{o.ic_isim}</SelectItem>)}
              </SelectContent>
            </Select>
            <DialogFooter><Button onClick={save} data-testid="oom-save-button" className="bg-amber-600 hover:bg-amber-700">Kaydet</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

// ============ MEMUR SATIRI ============
function OfficerRow({ officer: o, ranks, units, canFull, canManageUnit, canDeleteWarning, isSuperAdmin, onChanged }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`officer-row-${o.id}`}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-medium">{o.ic_isim}</span>
            {o.is_ps && <Badge className="bg-rose-600/20 text-rose-400 border-rose-600/40">Polis Şefi</Badge>}
            {o.is_yps && <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/40">Yard. Pol. Şefi</Badge>}
            {o.is_genel_birim_sefi && <Badge className="bg-sky-600/20 text-sky-400 border-sky-600/40">Birim Şefi</Badge>}
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-zinc-400">
          <div>@{o.discord_username}</div>
          <div className="text-zinc-600 font-mono">{o.discord_id}</div>
        </td>
        <td className="px-4 py-3 font-mono text-xs text-zinc-400">{o.hex_id}</td>
        <td className="px-4 py-3 text-xs">{o.rank_name}</td>
        <td className="px-4 py-3 text-xs">
          {o.units?.length ? o.units.map((u) => (
            <div key={u.unit_id} className="text-zinc-400">
              {u.unit_name} <span className="text-zinc-600">({u.position === "chief" ? "Şef" : u.position === "deputy_chief" ? "Yrd. Şef" : "Üye"})</span>
            </div>
          )) : <span className="text-zinc-600">—</span>}
        </td>
        <td className="px-4 py-3 text-xs text-center">{o.mazeret_count}</td>
        <td className="px-4 py-3 text-xs text-center">{o.rapor_count}</td>
        <td className="px-4 py-3 text-xs text-center">{o.mugshot_count}</td>
        <td className="px-4 py-3 text-xs text-center font-mono text-amber-400">{o.coin}</td>
        <td className="px-4 py-3 text-xs text-center">{o.warnings?.length || 0}</td>
        <td className="px-4 py-3">
          <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)} data-testid={`officer-expand-${o.id}`}>
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-zinc-900 bg-zinc-950/60">
          <td colSpan={11} className="px-4 py-4">
            <OfficerActions officer={o} ranks={ranks} units={units} canFull={canFull}
              canManageUnit={canManageUnit} canDeleteWarning={canDeleteWarning} isSuperAdmin={isSuperAdmin} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

// ============ GENİŞLETİLMİŞ MEMUR İŞLEMLERİ ============
function OfficerActions({ officer: o, ranks, units, canFull, canManageUnit, canDeleteWarning, isSuperAdmin, onChanged }) {
  const [icIsim, setIcIsim] = useState(o.ic_isim);
  const [coin, setCoin] = useState(o.coin);
  const [warningText, setWarningText] = useState("");
  const [rankId, setRankId] = useState(o.rank_id);
  const [unitId, setUnitId] = useState("");
  const [unitPosition, setUnitPosition] = useState("member");
  const [kickReason, setKickReason] = useState("");
  const [kickOpen, setKickOpen] = useState(false);

  const saveBasic = async () => {
    try {
      await api.put(`/memur/officers/${o.id}`, { ic_isim: icIsim, coin: Number(coin) });
      toast.success("Güncellendi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const addWarning = async () => {
    if (!warningText.trim()) return;
    try {
      await api.post(`/memur/officers/${o.id}/warnings`, { text: warningText });
      toast.success("Uyarı eklendi"); setWarningText(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const deleteWarning = async (warningId) => {
    if (!window.confirm("Bu uyarı silinsin mi?")) return;
    try {
      await api.delete(`/memur/officers/${o.id}/warnings/${warningId}`);
      toast.success("Uyarı silindi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const changeRank = async () => {
    try {
      await api.put(`/memur/officers/${o.id}/rank`, { rank_id: rankId });
      toast.success("Rütbe güncellendi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const assignUnit = async () => {
    if (!unitId) return;
    try {
      await api.post(`/memur/officers/${o.id}/units`, { unit_id: unitId, position: unitPosition });
      toast.success("Birim güncellendi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const removeUnit = async (uid) => {
    try {
      await api.delete(`/memur/officers/${o.id}/units/${uid}`);
      toast.success("Birimden çıkarıldı"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const setFlag = async (key, value) => {
    try {
      await api.put(`/memur/officers/${o.id}/flags`, null, { params: { [key]: value } });
      toast.success("Yetki güncellendi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const kick = async () => {
    if (!kickReason.trim()) { toast.error("Sebep girmelisiniz"); return; }
    try {
      await api.delete(`/memur/officers/${o.id}`, { data: { reason: kickReason } });
      toast.success("Memur ihraç edildi"); setKickOpen(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {canFull && (
        <div className="space-y-3">
          <div className="text-[10px] font-mono text-zinc-500 tracking-widest">// TEMEL BİLGİLER</div>
          <div className="flex gap-2">
            <Input value={icIsim} onChange={(e) => setIcIsim(e.target.value)} placeholder="IC İsim"
              data-testid={`officer-icisim-${o.id}`} className="bg-zinc-900 border-zinc-800" />
            <Input type="number" value={coin} onChange={(e) => setCoin(e.target.value)} placeholder="Coin"
              data-testid={`officer-coin-${o.id}`} className="bg-zinc-900 border-zinc-800 w-32" />
            <Button size="sm" onClick={saveBasic} data-testid={`officer-save-${o.id}`}><Pencil className="w-3 h-3 mr-1" />Kaydet</Button>
          </div>

          <div className="flex gap-2">
            <Input value={warningText} onChange={(e) => setWarningText(e.target.value)} placeholder="Uyarı metni"
              data-testid={`officer-warning-input-${o.id}`} className="bg-zinc-900 border-zinc-800" />
            <Button size="sm" variant="outline" onClick={addWarning} data-testid={`officer-warning-add-${o.id}`}
              className="border-amber-800 text-amber-400"><TriangleAlert className="w-3 h-3 mr-1" />Uyar</Button>
          </div>
          {o.warnings?.length > 0 && (
            <div className="text-xs text-zinc-500 space-y-1 max-h-24 overflow-y-auto">
              {o.warnings.map((w) => (
                <div key={w.id} className="flex items-center justify-between gap-2">
                  <span>• {w.text} <span className="text-zinc-700">({w.by_name})</span></span>
                  {canDeleteWarning && (
                    <button onClick={() => deleteWarning(w.id)} data-testid={`officer-warning-delete-${o.id}-${w.id}`}
                      className="text-zinc-600 hover:text-rose-400 transition-colors shrink-0" title="Uyarıyı sil">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-900">

            {isSuperAdmin && (
              <Button
                size="sm"
                variant={o.is_ps ? "default" : "outline"}
                onClick={() => setFlag("is_ps", !o.is_ps)}
                data-testid={`officer-toggle-ps-${o.id}`}
                className={o.is_ps ? "bg-rose-600" : "border-rose-800 text-rose-400"}
              >
                Polis Şefi {o.is_ps ? "Al" : "Ver"}
              </Button>
            )}

            {(isSuperAdmin || o.is_ps) && (
              <Button
                size="sm"
                variant={o.is_yps ? "default" : "outline"}
                onClick={() => setFlag("is_yps", !o.is_yps)}
                data-testid={`officer-toggle-yps-${o.id}`}
                className={o.is_yps ? "bg-amber-600" : "border-amber-800 text-amber-400"}
              >
                Yard. Pol. Şefi {o.is_yps ? "Al" : "Ver"}
              </Button>
            )}

            {canFull && (
              <Button
                size="sm"
                variant={o.is_genel_birim_sefi ? "default" : "outline"}
                onClick={() => setFlag("is_genel_birim_sefi", !o.is_genel_birim_sefi)}
                data-testid={`officer-toggle-gbs-${o.id}`}
                className={o.is_genel_birim_sefi ? "bg-sky-600" : "border-sky-800 text-sky-400"}
              >
                Birim Şefi {o.is_genel_birim_sefi ? "Al" : "Ver"}
              </Button>
            )}

            <Dialog open={kickOpen} onOpenChange={setKickOpen}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setKickOpen(true)}
                data-testid={`officer-kick-${o.id}`}
                className="border-rose-800 text-rose-400 hover:bg-rose-950/30"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                İhraç Et
              </Button>

              <DialogContent className="bg-zinc-950 border-zinc-800">
                <DialogHeader>
                  <DialogTitle>{o.ic_isim} kişisini ihraç et</DialogTitle>
                </DialogHeader>

                <Textarea
                  value={kickReason}
                  onChange={(e) => setKickReason(e.target.value)}
                  placeholder="İhraç sebebi"
                  data-testid={`officer-kick-reason-${o.id}`}
                  className="bg-zinc-900 border-zinc-800"
                />

                <DialogFooter>
                  <Button
                    onClick={kick}
                    data-testid={`officer-kick-confirm-${o.id}`}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    İhraç Et
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="text-[10px] font-mono text-zinc-500 tracking-widest">// RÜTBE</div>
        {canFull ? (
          <div className="flex gap-2">
            <Select value={rankId} onValueChange={setRankId}>
              <SelectTrigger data-testid={`officer-rank-select-${o.id}`} className="bg-zinc-900 border-zinc-800 w-48"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {ranks.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={changeRank} data-testid={`officer-rank-save-${o.id}`}>Rütbeyi Değiştir</Button>
          </div>
        ) : <div className="text-xs text-zinc-500">{o.rank_name}</div>}

        <div className="text-[10px] font-mono text-zinc-500 tracking-widest pt-2">// BİRİM ATAMA</div>
        <div className="flex gap-2 flex-wrap">
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger data-testid={`officer-unit-select-${o.id}`} className="bg-zinc-900 border-zinc-800 w-36"><SelectValue placeholder="Birim" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={unitPosition} onValueChange={setUnitPosition}>
            <SelectTrigger data-testid={`officer-unitpos-select-${o.id}`} className="bg-zinc-900 border-zinc-800 w-32"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value="member">Üye</SelectItem>
              <SelectItem value="deputy_chief">Yrd. Şef</SelectItem>
              <SelectItem value="chief">Şef</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={assignUnit} disabled={!unitId || !canManageUnit(unitId, unitPosition)}
            data-testid={`officer-unit-assign-${o.id}`}><Users2 className="w-3 h-3 mr-1" />Ata</Button>
        </div>
        {o.units?.length > 0 && (
          <div className="space-y-1">
            {o.units.map((u) => (
              <div key={u.unit_id} className="flex items-center justify-between text-xs bg-zinc-900/50 px-2 py-1">
                <span>{u.unit_name} — {u.position === "chief" ? "Şef" : u.position === "deputy_chief" ? "Yrd. Şef" : "Üye"}</span>
                {canManageUnit(u.unit_id, u.position) && (
                  <button onClick={() => removeUnit(u.unit_id)} data-testid={`officer-unit-remove-${o.id}-${u.unit_id}`}
                    className="text-rose-400 hover:text-rose-300"><Trash2 className="w-3 h-3" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ AYARLAR (Yalnızca Yönetici/Kurucu) ============
function SettingsPanel({ ranks }) {
  const [settings, setSettings] = useState(null);
  const [maxLevel, setMaxLevel] = useState("");
  const [eligible, setEligible] = useState([]);

  useEffect(() => {
    api.get("/memur/settings").then((r) => {
      setSettings(r.data);
      setMaxLevel(String(r.data.genel_birim_sefi_max_rank_level));
      setEligible(r.data.approval_eligible_rank_ids || []);
    });
  }, []);

  const toggleEligible = (rankId) => {
    setEligible((prev) => (prev.includes(rankId) ? prev.filter((id) => id !== rankId) : [...prev, rankId]));
  };

  const save = async () => {
    try {
      await api.put("/memur/settings", {
        genel_birim_sefi_max_rank_level: Number(maxLevel),
        approval_eligible_rank_ids: eligible,
      });
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  if (!settings) return null;

  return (
    <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-6 max-w-2xl">
      <div>
        <Label className="text-xs font-mono">BİRİM ŞEFİ</Label>
        <p className="text-xs text-zinc-500 mt-1 mb-2">Birim Şefi, bu seviyeye kadar (dahil) rütbe terfi/tenzil verebilir.</p>
        <Input type="number" value={maxLevel} onChange={(e) => setMaxLevel(e.target.value)}
          data-testid="settings-max-rank-level-input" className="bg-zinc-900 border-zinc-800 w-32" />
      </div>

      <div>
        <Label className="text-xs font-mono">OLAY RAPORU / KANIT DEPOSU / MUGSHOT ONAYI YAPABİLECEK RÜTBELER</Label>
        <p className="text-xs text-zinc-500 mt-1 mb-2">Polis Şefi/Yard. Pol. Şefi bu listeden bağımsız her zaman onaylayabilir. Buradan seçilen rütbelerdeki memurlar da onaylayabilir.</p>
        <div className="space-y-2">
          {ranks.sort((a, b) => a.level - b.level).map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={eligible.includes(r.id)} onChange={() => toggleEligible(r.id)}
                data-testid={`settings-eligible-rank-${r.id}`} className="accent-rose-600" />
              <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700 w-8 justify-center text-[10px]">{r.level}</Badge>
              {r.name}
            </label>
          ))}
        </div>
      </div>

      <Button onClick={save} data-testid="settings-save-button" className="bg-rose-600 hover:bg-rose-700">Kaydet</Button>
    </Card>
  );
}
function RanksPanel({ ranks, onChanged }) {
  const [level, setLevel] = useState("");
  const [name, setName] = useState("");
  const [editing, setEditing] = useState({});

  const add = async () => {
    if (!level || !name.trim()) return;
    try {
      await api.post("/memur/ranks", { level: Number(level), name });
      toast.success("Rütbe eklendi"); setLevel(""); setName(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const rename = async (id) => {
    const newName = editing[id];
    if (!newName?.trim()) return;
    try {
      await api.put(`/memur/ranks/${id}`, { name: newName });
      toast.success("Rütbe güncellendi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu rütbe silinsin mi?")) return;
    try {
      await api.delete(`/memur/ranks/${id}`);
      toast.success("Silindi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4 max-w-2xl">
      <div className="flex gap-2">
        <Input type="number" value={level} onChange={(e) => setLevel(e.target.value)} placeholder="Seviye (1-20)"
          data-testid="rank-level-input" className="bg-zinc-900 border-zinc-800 w-32" />
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Rütbe adı"
          data-testid="rank-name-input" className="bg-zinc-900 border-zinc-800" />
        <Button onClick={add} data-testid="rank-add-button">Ekle</Button>
      </div>
      <div className="space-y-2">
        {ranks.sort((a, b) => a.level - b.level).map((r) => (
          <div key={r.id} className="flex items-center gap-2">
            <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700 w-10 justify-center">{r.level}</Badge>
            <Input defaultValue={r.name} onChange={(e) => setEditing({ ...editing, [r.id]: e.target.value })}
              data-testid={`rank-rename-input-${r.id}`} className="bg-zinc-900 border-zinc-800" />
            <Button size="sm" variant="outline" onClick={() => rename(r.id)} data-testid={`rank-rename-save-${r.id}`}>Kaydet</Button>
            <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`rank-delete-${r.id}`} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============ BİRİM YÖNETİMİ ============
function UnitsPanel({ units, onChanged }) {
  const [name, setName] = useState("");

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/memur/units", { name });
      toast.success("Birim eklendi"); setName(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu birim silinsin mi? (Tüm üyelikleri de kaldırılır)")) return;
    try {
      await api.delete(`/memur/units/${id}`);
      toast.success("Silindi"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4 max-w-2xl">
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Birim adı (örn. HSU)"
          data-testid="unit-name-input" className="bg-zinc-900 border-zinc-800" />
        <Button onClick={add} data-testid="unit-add-button">Ekle</Button>
      </div>
      <div className="space-y-2">
        {units.map((u) => (
          <div key={u.id} className="flex items-center justify-between bg-zinc-900/50 px-3 py-2">
            <span>{u.name}</span>
            <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`unit-delete-${u.id}`} className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
